import { call, put, select } from 'redux-saga/effects';
import moment from 'moment';
import { ApiError, ApiResponse } from 'store/api/types';
import { logoutRequest } from 'store/auth/actions';
import { turnMessageOn } from 'store/app/actions';
import { DataLogStatus, TaxReportCountry, VatTaxReportType } from 'store/app/enums';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { checkResponse } from 'store/utils';
import { renderErrorMessage } from 'utils/render-error-message';
import { fetchRunDataSuccess } from 'store/vat/actions';
import { AdjustStatus, VatRunData } from 'store/vat/models/vatRunData';
import { User } from 'store/auth/models';
import { getUserMembers } from 'store/user/selectors';
import { getOrgMappings } from 'store/organisation/selectors';
import { Mapping } from 'store/organisation/models';
import { getTifdReportById } from '../api';
import { findReturnLog, savedRoleIds, splitActivityUsers } from '../activity-users';
import { loadTifdReportFailure, previewTifdReportSuccess } from '../actions';
import { TifdReport } from '../models';
import { GetTifdReportRequestAction } from '../types';

const GENERATED_DATE_FORMAT = 'DD/MM/YYYY HH:mm:ss';
const PERIOD_DATE_FORMAT = 'DD/MM/YYYY';

const readReport = (response: ApiResponse): TifdReport | null => {
  const data = response?.data;
  if (data && typeof data === 'object' && 'report' in data && data.report) {
    return data.report as TifdReport;
  }
  if (data && typeof data === 'object' && Array.isArray(data.sheets)) {
    return data as unknown as TifdReport;
  }
  return null;
};

const runStatus = (status: string | null | undefined): string => {
  if (!status) return 'NOT_SAVED';
  if (Object.prototype.hasOwnProperty.call(DataLogStatus, status)) {
    return status;
  }
  const key = (Object.keys(DataLogStatus) as Array<keyof typeof DataLogStatus>).find(
    (item) => DataLogStatus[item] === status
  );
  return key ?? status;
};

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* loadTifdReportSaga(action: GetTifdReportRequestAction) {
  const {
    applicationConnectionId,
    reportId,
    connectionId,
    mappingId,
    currency,
    providerName,
    connectionName,
    shortCode,
  } = action.payload;

  try {
    if (!applicationConnectionId) {
      const message: MessageData = {
        title: 'TIFD report',
        description: 'This entity has no organisation id to load the report with.',
        type: MessageStates.ERROR,
      };
      yield put(turnMessageOn(message));
      yield put(loadTifdReportFailure());
      return;
    }

    const response: ApiResponse = yield call(
      getTifdReportById,
      applicationConnectionId,
      reportId
    );

    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const report = readReport(response);
    if (!report) {
      const message: MessageData = {
        title: 'TIFD report',
        description: 'The report response did not include a report.',
        type: MessageStates.ERROR,
      };
      yield put(turnMessageOn(message));
      yield put(loadTifdReportFailure());
      return;
    }

    const fromLabel = moment(report.periodStart).format(PERIOD_DATE_FORMAT);
    const toLabel = moment(report.periodEnd).format(PERIOD_DATE_FORMAT);
    const generatedDate = report.generatedDate
      ? moment.utc(report.generatedDate).format(GENERATED_DATE_FORMAT)
      : moment.utc().format(GENERATED_DATE_FORMAT);

    const members: User[] | undefined = yield select(getUserMembers);
    const mappings: Mapping[] | undefined = yield select(getOrgMappings);
    const { staff, clients } = splitActivityUsers(members);
    const savedRoles = savedRoleIds(
      findReturnLog(mappings, mappingId, report.id ?? reportId),
      members
    );

    const vatRunData: VatRunData = {
      adjustStatus: AdjustStatus.ADJUSTED_TOTALS,
      mappingId,
      tokenId: connectionId,
      fromDate: report.periodStart,
      toDate: report.periodEnd,
      currency: report.currency || currency || '',
      jsonMapping: '',
      openDataLogId: '',
      dataLogId: report.id ?? reportId,
      generationPeriod: `${fromLabel} - ${toLabel}`,
      generatedDate,
      validRange: true,
      taxReportCountry: TaxReportCountry.MALTA,
      isPeriodClosed: true,
      reportingTypeId: report.reportTypeId || '',
      taxReportTypeName: report.catalogueName || '',
      isFirstTime: false,
      connectionName: connectionName ?? '',
      savable: true,
      taxType: VatTaxReportType.VAT_RETURN,
      shortCode: shortCode ?? '',
      status: runStatus(report.status),
      preparerId: savedRoles.preparerId,
      reviewerId: savedRoles.reviewerId,
      client: '',
      clientId: savedRoles.clientId ?? '',
      staff,
      clients,
      providerName: providerName ?? report.sourceType ?? '',
      taxRates: [],
      totalsSaved: false,
      updateFromProvider: false,
    };

    yield put(
      previewTifdReportSuccess(report, {
        connectionId,
        mappingId,
        applicationConnectionId,
      })
    );
    yield put(fetchRunDataSuccess(vatRunData));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(loadTifdReportFailure());
  }
}
